import { motion } from 'framer-motion'
import { GradientBackground } from './GradientBackground'
import { useProjects } from '../hooks/useProjects'

export function ProjectLavazza() {
  const { getRandomProject } = useProjects()
  
  const handleNextProject = () => {
    const randomProject = getRandomProject('lavazza-professional')
    if (randomProject) {
      window.location.hash = randomProject.slug
    }
  }

  return (
    <GradientBackground>
      {/* Hero Section - Cover image at 65% height */}
      <section className="relative h-[65vh] w-full">
        <img 
          src="/assets/projects/LAVAZZA_PROFESSIONAL/NUVOLA_PRO.webp" 
          alt="Lavazza Professional"
          className="w-full h-full object-cover object-center"
        />
      </section>

      {/* Project Info */}
      <section className="py-20 px-4" style={{ background: 'linear-gradient(to bottom, transparent 0%, #CFCFCF 100%)' }}>
        <div className="container mx-auto max-w-4xl">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16"
          >
            <div>
              <h3 className="text-sm uppercase tracking-wider text-white/60 mb-2">Client</h3>
              <p className="text-lg font-medium text-white">Lavazza</p>
            </div>
            <div>
              <h3 className="text-sm uppercase tracking-wider text-white/60 mb-2">Services</h3>
              <p className="text-lg font-medium text-white">Graphic Design, Visual Communication</p>
            </div>
            <div>
              <h3 className="text-sm uppercase tracking-wider text-white/60 mb-2">Year</h3>
              <p className="text-lg font-medium text-white">2019</p>
            </div>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="prose prose-lg max-w-none"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-8">The Project</h2>
            <p className="text-xl text-white/80 leading-relaxed mb-12">
              Graphic design and visual communication project for Lavazza Professional. 
              I developed product presentation materials, marketing supports, and corporate 
              identity elements for the professional division of the brand. The work includes 
              the design of posters, presentations, point-of-sale materials, and digital assets.
            </p>

            {/* Images Grid - 2 columns like DUI */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
              <div className="space-y-8">
                <img 
                  src="/assets/projects/LAVAZZA_PROFESSIONAL/NUVOLA_PRO.webp" 
                  alt="Lavazza Nuvola Pro"
                  className="w-full rounded-lg shadow-lg"
                />
                <img 
                  src="/assets/projects/LAVAZZA_PROFESSIONAL/COLORE.webp" 
                  alt="Lavazza Color System"
                  className="w-full rounded-lg shadow-lg"
                />
                <img 
                  src="/assets/projects/LAVAZZA_PROFESSIONAL/POSTER_ESEC_Pagina_3.webp" 
                  alt="Lavazza Poster Design"
                  className="w-full rounded-lg shadow-lg"
                />
                <img 
                  src="/assets/projects/LAVAZZA_PROFESSIONAL/IMG_20190121_193720.webp" 
                  alt="Lavazza Image 1"
                  className="w-full rounded-lg shadow-lg"
                />
                <img 
                  src="/assets/projects/LAVAZZA_PROFESSIONAL/book_inner-copia.webp" 
                  alt="Lavazza Book Inner"
                  className="w-full rounded-lg shadow-lg"
                />
              </div>
              <div className="space-y-8">
                <img 
                  src="/assets/projects/LAVAZZA_PROFESSIONAL/caschetto.webp" 
                  alt="Lavazza Caschetto"
                  className="w-full rounded-lg shadow-lg"
                />
                <img 
                  src="/assets/projects/LAVAZZA_PROFESSIONAL/POSTER_ESEC_Pagina_4.webp" 
                  alt="Lavazza Poster Design"
                  className="w-full rounded-lg shadow-lg"
                />
                <img 
                  src="/assets/projects/LAVAZZA_PROFESSIONAL/PRESENTAZIONE_28-11.webp" 
                  alt="Lavazza Presentation"
                  className="w-full rounded-lg shadow-lg"
                />
                <img 
                  src="/assets/projects/LAVAZZA_PROFESSIONAL/IMG_20190121_194405.webp" 
                  alt="Lavazza Image 2"
                  className="w-full rounded-lg shadow-lg"
                />
                <img 
                  src="/assets/projects/LAVAZZA_PROFESSIONAL/IMG_20190121_194810.webp" 
                  alt="Lavazza Image 3"
                  className="w-full rounded-lg shadow-lg"
                />
              </div>
            </div>

            <h2 className="text-3xl md:text-4xl font-bold text-white mb-8">The Approach</h2>
            <p className="text-xl text-white/80 leading-relaxed mb-12">
              The work required attention to detail and ability to adapt the visual 
              language to different communicative needs, always maintaining consistency with the values 
              and aesthetics of the Lavazza brand. Each material was designed to communicate 
              effectively with the professional target of the brand.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Navigation Footer */}
      <section className="py-16 px-4 bg-[#CFCFCF]">
        <div className="container mx-auto max-w-4xl">
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {/* Back to Works */}
            <a 
              href="/#works"
              className="group relative inline-flex items-center justify-center gap-2 px-8 py-4 text-white text-lg font-medium rounded-lg overflow-hidden cursor-pointer transition-transform duration-300 hover:scale-105" style={{ background: 'linear-gradient(90deg, #7ec876 0%, #4da6ff 50%, #7ec876 100%)', backgroundSize: '200% 100%', animation: 'gradient-shift 3s ease infinite' }}
            >
              <span className="relative z-10 flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="m15 18-6-6 6-6"/>
                </svg>
                Back to Works
              </span>
              <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300" />
            </a>

            {/* Next Project */}
            <button 
              onClick={handleNextProject}
              className="group relative inline-flex items-center justify-center gap-2 px-8 py-4 text-white text-lg font-medium rounded-lg overflow-hidden cursor-pointer transition-transform duration-300 hover:scale-105" style={{ background: 'linear-gradient(90deg, #7ec876 0%, #4da6ff 50%, #7ec876 100%)', backgroundSize: '200% 100%', animation: 'gradient-shift 3s ease infinite' }}
            >
              <span className="relative z-10 flex items-center gap-2">
                Next Project
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="m9 18 6-6-6-6"/>
                </svg>
              </span>
              <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300" />
            </button>
          </div>
        </div>
      </section>
      
      <style>{`
        @keyframes gradient-shift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
      `}</style>
    </GradientBackground>
  )
}
